import type { NumberRangeBounds, NumberRangeValue } from "@/components/NumberRangeFilter";

export type { NumberRangeValue, NumberRangeBounds };

/** Colunas numéricas da listagem que aceitam filtro de intervalo. */
export type ProductNumericColumn =
  | "game_quantity"
  | "dez_quantity"
  | "quota_quantity"
  | "total_without_tariff"
  | "total_with_tariff"
  | "game_percentage"
  | "tariff"
  | "sale_price"
  | "price_without_tariff"
  | "commission_by_quote";

export type ProductNumericColumnConfig = {
  label: string;
  decimals: number;
  format: "number" | "currency";
};

export const PRODUCT_NUMERIC_COLUMNS: Record<
  ProductNumericColumn,
  ProductNumericColumnConfig
> = {
  game_quantity: { label: "Jogos", decimals: 0, format: "number" },
  dez_quantity: { label: "Dezenas", decimals: 0, format: "number" },
  quota_quantity: { label: "Cotas", decimals: 0, format: "number" },
  total_without_tariff: { label: "Total sem Tarifa", decimals: 2, format: "currency" },
  total_with_tariff: { label: "Valor Total", decimals: 2, format: "currency" },
  game_percentage: { label: "Percentual do Jogo", decimals: 2, format: "number" },
  tariff: { label: "Tarifa", decimals: 2, format: "currency" },
  sale_price: { label: "Preço de Venda", decimals: 2, format: "currency" },
  price_without_tariff: { label: "Preço sem Tarifa", decimals: 2, format: "currency" },
  commission_by_quote: { label: "Comissão por Cota", decimals: 2, format: "currency" },
};

export type LotteryOption = {
  id: number;
  name: string;
};

export type ProductsFilters = {
  name?: string;
  lottery_id?: string[];
  ranges?: Partial<Record<ProductNumericColumn, NumberRangeValue>>;
};

/**
 * Limites reais (menor/maior valor existente) de cada coluna numérica,
 * calculados pelo backend e devolvidos em `limits` na resposta da listagem.
 * `min`/`max` nulos = não existem produtos para aquela coluna no recorte atual.
 */
export type ProductFilterLimits = Partial<
  Record<ProductNumericColumn, NumberRangeBounds>
>;

export const emptyProductsFilters: ProductsFilters = {};

const isNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

export const isRangeActive = (range?: NumberRangeValue): boolean =>
  !!range && (isNumber(range.min) || isNumber(range.max));

export const hasActiveFilters = (filters: ProductsFilters): boolean => {
  if (filters.name) return true;
  if (filters.lottery_id?.length) return true;
  return Object.values(filters.ranges ?? {}).some(isRangeActive);
};

const formatValue = (
  value: number,
  { decimals, format }: ProductNumericColumnConfig
) =>
  format === "currency"
    ? new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(value)
    : new Intl.NumberFormat("pt-BR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: decimals,
      }).format(value);

/** Texto curto do intervalo selecionado, exibido abaixo do título da coluna. */
export const describeRange = (
  range: NumberRangeValue | undefined,
  config: ProductNumericColumnConfig
): string | null => {
  if (!isRangeActive(range)) return null;

  const min = isNumber(range?.min) ? formatValue(range!.min!, config) : null;
  const max = isNumber(range?.max) ? formatValue(range!.max!, config) : null;

  if (min && max) return min === max ? min : `${min} - ${max}`;
  if (min) return `≥ ${min}`;
  return `≤ ${max}`;
};

export const setRangeFilter = (
  filters: ProductsFilters,
  column: ProductNumericColumn,
  range: NumberRangeValue
): ProductsFilters => {
  const nextRanges = { ...(filters.ranges ?? {}) };

  if (isRangeActive(range)) {
    nextRanges[column] = range;
  } else {
    delete nextRanges[column];
  }

  return { ...filters, ranges: nextRanges };
};

const clamp = (value: number, lower: number, upper: number) =>
  Math.min(Math.max(value, lower), upper);

/**
 * Reconcilia os intervalos já selecionados com os limites que acabaram de
 * chegar da API (eles mudam quando loteria/nome mudam o conjunto de dados).
 *
 * - valor fora do novo limite é ajustado para o limite;
 * - intervalo que passa a cobrir todo o range vira "sem filtro";
 * - coluna sem dados (min/max nulos) perde o filtro, já que não há referência.
 *
 * Retorna o MESMO objeto quando nada muda, para não disparar refetch em loop.
 */
export const clampFiltersToLimits = (
  filters: ProductsFilters,
  limits: ProductFilterLimits | null
): ProductsFilters => {
  const currentRanges = filters.ranges;
  if (!limits || !currentRanges) return filters;

  const columns = Object.keys(currentRanges) as ProductNumericColumn[];
  if (columns.length === 0) return filters;

  const nextRanges: NonNullable<ProductsFilters["ranges"]> = {};
  let changed = false;

  columns.forEach((column) => {
    const range = currentRanges[column];

    if (!isRangeActive(range)) {
      changed = true;
      return;
    }

    const bounds = limits[column];

    if (!bounds || !isNumber(bounds.min) || !isNumber(bounds.max)) {
      changed = true;
      return;
    }

    const { decimals } = PRODUCT_NUMERIC_COLUMNS[column];
    const round = (value: number) => Number(value.toFixed(decimals));

    const nextMin = isNumber(range!.min)
      ? round(clamp(range!.min!, bounds.min, bounds.max))
      : undefined;
    const nextMax = isNumber(range!.max)
      ? round(clamp(range!.max!, bounds.min, bounds.max))
      : undefined;

    if (
      isNumber(nextMin) &&
      isNumber(nextMax) &&
      nextMin <= bounds.min &&
      nextMax >= bounds.max
    ) {
      changed = true;
      return;
    }

    if (nextMin !== range!.min || nextMax !== range!.max) changed = true;

    nextRanges[column] = {
      ...(isNumber(nextMin) ? { min: nextMin } : {}),
      ...(isNumber(nextMax) ? { max: nextMax } : {}),
    };
  });

  return changed ? { ...filters, ranges: nextRanges } : filters;
};
